/**
 * @file TodoGroupingControls.tsx
 * @brief To-do grouping UI (F1a, DES 31-10-2026, ADR-062), shared by the
 *        meeting To-dos tab and My to-dos (web + Mac app).
 *
 * @details
 * - `TodoViewMenu`: the small, secondary view switch ("By category ▾"), a
 *   menu, never a tab bar. The pick is remembered by the caller.
 * - `TodoGroupHeading`: a collapsible heading with its count; an optional
 *   ••• menu (category: Rename, Merge into…; topic: Rename).
 * - `MoveTodoMenu`: the chip on a to-do ("Finance" ▾): move to another
 *   category or type a new one; on the meeting screen also another topic.
 * Menus, not centred dialogs (webpanel-searchable-menu-list). User choices
 * win on the server and are fed back to the AI.
 *
 * @references todoViews.ts, todoGroupingApi.ts, docs/product/todo-grouping.md
 * @author DES (prepared with Claude)
 * @date 31-10-2026
 */

import { useState, type KeyboardEvent, type ReactNode } from "react";
import { Check, ChevronDown, ChevronRight, Combine, LayoutList, MoreHorizontal, Pencil, Plus, Tag } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/DropdownMenu";
import { SearchableMenuList } from "@/components/ui/SearchableMenuList";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { TODO_VIEW_LABELS, type TodoViewKey } from "./todoViews";
import {
  useMergeTodoCategories,
  useMoveTodo,
  useRenameTodoCategory,
  useRenameTodoTopic,
  type TodoCategory,
} from "./todoGroupingApi";

const failToast = (title: string) => (err: Error) =>
  toast({ title, description: err.message || undefined, variant: "destructive" });

// ── View switch ──────────────────────────────────────────────────────────────

export function TodoViewMenu({
  value,
  options,
  onChange,
  size = "sm",
}: {
  value: TodoViewKey;
  options: TodoViewKey[];
  onChange: (next: TodoViewKey) => void;
  /** "sm" = the meeting band; "lg" = the My to-dos page pill. */
  size?: "sm" | "lg";
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={`View: ${TODO_VIEW_LABELS[value]}`}
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full font-semibold text-app-text transition-colors",
            "bg-[#ECEBE9] hover:bg-[#E2E0DD] dark:bg-app-quaternary dark:hover:bg-white/10",
            size === "lg" ? "h-[50px] gap-2 px-5 text-[15px]" : "h-8 px-3 text-xs",
          )}
        >
          <LayoutList className={size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5"} strokeWidth={1.75} />
          {TODO_VIEW_LABELS[value]}
          <ChevronDown className={cn(size === "lg" ? "h-4 w-4" : "h-3.5 w-3.5", "opacity-60")} strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-52 p-1.5">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">View</DropdownMenuLabel>
        {options.map((v) => (
          <DropdownMenuItem
            key={v}
            onSelect={() => {
              onChange(v);
              setOpen(false);
            }}
            className="flex items-center justify-between gap-2"
          >
            {TODO_VIEW_LABELS[v]}
            {v === value ? <Check className="h-4 w-4 text-brand" /> : null}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

// ── Heading ──────────────────────────────────────────────────────────────────

export function TodoGroupHeading({
  label,
  count,
  collapsed,
  onToggle,
  tone,
  meta,
  menu,
  className,
}: {
  label: string;
  count: number;
  collapsed: boolean;
  onToggle: () => void;
  tone?: "overdue";
  /** Small text after the count (e.g. a meeting date, "2 overdue"). */
  meta?: ReactNode;
  /** The ••• menu, when the heading can be edited. */
  menu?: ReactNode;
  className?: string;
}) {
  const Chevron = collapsed ? ChevronRight : ChevronDown;
  return (
    <div className={cn("flex min-w-0 items-center gap-1", className)}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md py-1 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <Chevron className="h-3.5 w-3.5 shrink-0 text-app-text-secondary" strokeWidth={2} />
        <span
          dir="auto"
          className={cn(
            "truncate text-[13px] font-semibold leading-4",
            tone === "overdue" ? "text-destructive" : "text-app-text-secondary",
          )}
        >
          {label}
        </span>
        <span className="shrink-0 text-[13px] text-app-text-secondary">· {count}</span>
        {meta}
        <span aria-hidden className="ml-1 h-px min-w-4 flex-1 bg-app-text/[0.14]" />
      </button>
      {menu}
    </div>
  );
}

/** A one-line text field inside a menu: Enter saves, Escape closes. */
function MenuTextField({
  initial,
  placeholder,
  saveLabel,
  busy,
  onSave,
}: {
  initial: string;
  placeholder: string;
  saveLabel: string;
  busy?: boolean;
  onSave: (value: string) => void;
}) {
  const [value, setValue] = useState(initial);
  const submit = () => {
    const v = value.trim();
    if (v) onSave(v);
  };
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Radix typeahead must not take the letters (see SearchableMenuList).
    if (e.key !== "Escape" && e.key !== "Tab") e.stopPropagation();
    if (e.key === "Enter") {
      e.preventDefault();
      submit();
    }
  };
  return (
    <div className="flex items-center gap-1.5 px-1 pb-1">
      <input
        autoFocus
        dir="auto"
        value={value}
        maxLength={40}
        placeholder={placeholder}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={onKeyDown}
        className="h-9 min-w-0 flex-1 rounded-full bg-field px-3 text-sm text-app-text outline-none placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring"
      />
      <button
        type="button"
        disabled={busy || !value.trim()}
        onClick={submit}
        className="h-9 shrink-0 rounded-full bg-brand px-3 text-sm font-semibold text-white disabled:opacity-50"
      >
        {saveLabel}
      </button>
    </div>
  );
}

function HeadingMenuButton({
  label,
  children,
  onClosed,
}: {
  label: string;
  children: (close: () => void) => ReactNode;
  /** Called whenever the menu closes (reset any inner step). */
  onClosed?: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) onClosed?.();
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label={label}
          title={label}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-app-text-secondary hover:bg-black/[0.04] hover:text-app-text dark:hover:bg-white/[0.06]"
        >
          <MoreHorizontal className="h-4 w-4" strokeWidth={1.75} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 p-1.5">
        {children(() => setOpen(false))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** ••• on a category heading: Rename (all tasks, future meetings), Merge into… */
export function CategoryHeadingMenu({
  categoryId,
  name,
  categories,
}: {
  categoryId: string;
  name: string;
  categories: TodoCategory[];
}) {
  const [mode, setMode] = useState<"menu" | "rename" | "merge">("menu");
  const rename = useRenameTodoCategory();
  const merge = useMergeTodoCategories();
  const others = categories.filter((c) => c.id !== categoryId);
  return (
    <HeadingMenuButton label={`Edit category ${name}`} onClosed={() => setMode("menu")}>
      {(close) => {
        const done = () => {
          close();
          setMode("menu");
        };
        if (mode === "rename") {
          return (
            <>
              <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
                Rename category
              </DropdownMenuLabel>
              <MenuTextField
                initial={name}
                placeholder="Category name"
                saveLabel="Save"
                busy={rename.isPending}
                onSave={(v) =>
                  rename.mutate(
                    { categoryId, name: v },
                    {
                      onSuccess: (r) => {
                        toast({ title: r?.merged ? `Merged into ${r.category?.name ?? v}` : `Renamed to ${v}` });
                        done();
                      },
                      onError: failToast("Could not rename this category"),
                    },
                  )
                }
              />
              <p className="px-2 pb-1 text-[11px] text-muted-foreground">Changes it on every task and future meetings.</p>
            </>
          );
        }
        if (mode === "merge") {
          return (
            <SearchableMenuList
              title={`Merge ${name} into`}
              items={others.map((c) => ({ value: c.id, label: c.name }))}
              emptyText="No other category yet."
              onSelect={(intoId) =>
                merge.mutate(
                  { fromId: categoryId, intoId },
                  {
                    onSuccess: () => {
                      toast({ title: `Merged into ${others.find((c) => c.id === intoId)?.name ?? "category"}` });
                      done();
                    },
                    onError: failToast("Could not merge"),
                  },
                )
              }
            />
          );
        }
        return (
          <>
            <DropdownMenuItem onSelect={(e) => { e.preventDefault(); setMode("rename"); }}>
              <Pencil className="mr-2 h-4 w-4" strokeWidth={1.75} /> Rename…
            </DropdownMenuItem>
            <DropdownMenuItem disabled={others.length === 0} onSelect={(e) => { e.preventDefault(); setMode("merge"); }}>
              <Combine className="mr-2 h-4 w-4" strokeWidth={1.75} /> Merge into…
            </DropdownMenuItem>
          </>
        );
      }}
    </HeadingMenuButton>
  );
}

/** ••• on a topic heading (meeting screen): Rename. */
export function TopicHeadingMenu({ meetingId, topic }: { meetingId: string; topic: string }) {
  const rename = useRenameTodoTopic();
  return (
    <HeadingMenuButton label={`Rename heading ${topic}`}>
      {(close) => (
        <>
          <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground">
            Rename heading
          </DropdownMenuLabel>
          <MenuTextField
            initial={topic}
            placeholder="Heading"
            saveLabel="Save"
            busy={rename.isPending}
            onSave={(to) =>
              rename.mutate(
                { meetingId, from: topic, to },
                { onSuccess: () => close(), onError: failToast("Could not rename this heading") },
              )
            }
          />
        </>
      )}
    </HeadingMenuButton>
  );
}

// ── Move one to-do ───────────────────────────────────────────────────────────

/**
 * The chip on a to-do: its category (or "Add category"). Opens a menu to
 * move it to another category or a new one; with `topics`, also to another
 * topic (meeting screen).
 */
export function MoveTodoMenu({
  meetingId,
  todoId,
  category,
  categoryId,
  topic,
  categories,
  topics,
}: {
  meetingId: string;
  todoId: string;
  category?: string | null;
  categoryId?: string | null;
  topic?: string | null;
  categories: TodoCategory[];
  /** The meeting's topics; omitted on My to-dos. */
  topics?: string[];
}) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState<"category" | "topic">("category");
  const [query, setQuery] = useState("");
  const move = useMoveTodo();
  const typed = query.trim();
  const list = mode === "category" ? categories.map((c) => ({ value: c.id, label: c.name })) : (topics ?? []).map((t) => ({ value: t, label: t }));
  const shown = typed ? list.filter((i) => i.label.toLowerCase().includes(typed.toLowerCase())) : list;
  const exact = list.some((i) => i.label.trim().toLowerCase() === typed.toLowerCase());
  const save = (vars: { categoryId?: string; categoryName?: string; topic?: string }) =>
    move.mutate(
      { meetingId, todoId, ...vars },
      {
        onSuccess: () => {
          setOpen(false);
          setQuery("");
        },
        onError: failToast("Could not move this to-do"),
      },
    );
  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) {
          setQuery("");
          setMode("category");
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          title="Move to another category"
          className="inline-flex h-[22px] max-w-[12rem] items-center gap-1 rounded-full bg-black/[0.04] px-2 text-[11px] font-medium text-foreground transition-colors hover:bg-black/[0.08] dark:bg-white/[0.06] dark:hover:bg-white/[0.1]"
        >
          <Tag className="h-3 w-3 shrink-0" strokeWidth={1.75} />
          <span dir="auto" className="truncate">{category?.trim() || "Add category"}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className="w-72 p-1.5"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {topics ? (
          <div className="mb-1 flex gap-1 px-1">
            {(["category", "topic"] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => {
                  setMode(m);
                  setQuery("");
                }}
                className={cn(
                  "h-7 flex-1 rounded-full text-xs font-semibold",
                  mode === m ? "bg-brand text-white" : "bg-field text-app-text-secondary",
                )}
              >
                {m === "category" ? "Category" : "Topic"}
              </button>
            ))}
          </div>
        ) : null}
        <SearchableMenuList
          key={mode}
          title={mode === "category" ? "Move to category" : "Move to topic"}
          items={shown}
          selected={mode === "category" ? (categoryId ? [categoryId] : []) : topic ? [topic] : []}
          query={query}
          onQueryChange={setQuery}
          searchPlaceholder={mode === "category" ? "Find or type a new category" : "Find or type a new topic"}
          emptyText={mode === "category" ? "No categories yet. Type one." : "No topics yet. Type one."}
          header={
            typed && !exact ? (
              <DropdownMenuItem
                onSelect={(e) => {
                  e.preventDefault();
                  save(mode === "category" ? { categoryName: typed } : { topic: typed });
                }}
              >
                <Plus className="mr-2 h-4 w-4 text-brand" />
                New {mode}: <span dir="auto" className="ml-1 font-semibold">{typed}</span>
              </DropdownMenuItem>
            ) : undefined
          }
          onSelect={(value) => save(mode === "category" ? { categoryId: value } : { topic: value })}
        />
        {mode === "category" ? (
          <>
            <DropdownMenuSeparator />
            <p className="px-2 py-1 text-[11px] text-muted-foreground">Your choice is kept and teaches future meetings.</p>
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
